/**
 * Challenge Game Page
 * Handles series of games within a challenge
 * NOW USING REST API FOR GAME STATE (database-driven instead of socket events)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getSocket } from '../services/socket';
import '../styles/Flappy.css';
import '../styles/ChallengeGame.css';

function ChallengeGame() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [challenge, setChallenge] = useState(null);
  const [progress, setProgress] = useState(null);
  const [currentGame, setCurrentGame] = useState(null);
  const [currentGameNumber, setCurrentGameNumber] = useState(1);
  const [gameState, setGameState] = useState('waiting'); // waiting, ready, rolling, rolled, result
  const [myRoll, setMyRoll] = useState(null);
  const [opponentRoll, setOpponentRoll] = useState(null);
  const [gameResult, setGameResult] = useState(null);
  const [opponentProfile, setOpponentProfile] = useState(null);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollingIntervalRef = useRef(null);
  const waitForOpponentRef = useRef(null);
  const [isWaitingForOpponent, setIsWaitingForOpponent] = useState(false);

  const buildFallbackProgress = (challengeData, games = []) => {
    if (!challengeData) return null;
    const playedGames = (games || []).filter((g) => Boolean(g?.result_determined));
    const creatorId = Number(challengeData.creator_id || 0);
    const opponentId = Number(challengeData.opponent_id || 0);

    return {
      id: challengeData.id,
      creator_id: challengeData.creator_id,
      opponent_id: challengeData.opponent_id,
      num_games: Number(challengeData.num_games || 1),
      games_played: playedGames.length,
      creator_wins: playedGames.filter((g) => Number(g?.winner_id || 0) === creatorId).length,
      opponent_wins: playedGames.filter((g) => Number(g?.winner_id || 0) === opponentId).length,
      ties: playedGames.filter((g) => g?.winner_id == null).length,
    };
  };

  useEffect(() => {
    fetchChallengeData();
  }, [challengeId]);

  // Set up socket listener for real-time notifications only (not game logic)
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !user) {
      console.log('⚠️ Socket not ready or user not loaded');
      return;
    }

    console.log(`🔌 Socket ready for challenge #${challengeId}, user ${user.id}`);

    // Join challenge room for notifications only
    socket.emit('challenge:player_joined', { challengeId: parseInt(challengeId), userId: user.id });
    console.log(`📍 Joined challenge #${challengeId}`);

    // Listen only for notifications (optional sounds/alerts)
    socket.on('challenge:both_ready', (data) => {
      if (Number(data.id) === Number(challengeId)) {
        console.log('✓ Opponent is ready');
        setOpponentConnected(true);
        setGameState('ready');
        
        // Reload games list in case it was created after we first loaded
        console.log('🔄 Refreshing games list since opponent ready...');
        fetchChallengeData();
      }
    });

    return () => {
      socket.off('challenge:both_ready');
    };
  }, [challengeId, user]);

  const fetchChallengeData = async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!silent) setLoading(true);
      const challengeRes = await api.get(`/challenges/${challengeId}`);
      let progressData = null;
      try {
        const progressRes = await api.get(`/challenges/${challengeId}/progress`);
        progressData = progressRes.data;
      } catch (progressErr) {
        console.error('Failed to fetch challenge progress, falling back to derived progress:', progressErr);
      }

      setChallenge(challengeRes.data);

      // If the challenge is already completed (e.g. forfeited), do not keep showing the dice UI.
      // Users can still land on this route via browser back button.
      if (challengeRes.data?.status === 'completed') {
        stopPolling();
        setGameState('finished');
        setIsWaitingForOpponent(false);
      }

      // Load the current game
      try {
        const gamesRes = await api.get(`/challenges/${challengeId}/games`);
        const games = gamesRes.data || [];
        
        console.log(`📊 Found ${games.length} games for challenge #${challengeId}`);
        
        if (games.length > 0) {
          // Find the first incomplete game (result_determined = false)
          const currentGame = games.find(g => !g.result_determined);
          
          if (currentGame) {
            console.log(`✓ Setting current game:`, currentGame);
            setCurrentGame(currentGame);
          } else {
            // All games done, use the last one
            console.log(`ℹ All games completed, using last game`);
            setCurrentGame(games[games.length - 1]);
          }
        } else {
          console.warn('⚠️ No games found for this challenge');
          setCurrentGame(null);
        }
        setProgress(progressData || buildFallbackProgress(challengeRes.data, games));
      } catch (e) {
        console.error('❌ Error loading games:', e);
        setProgress(progressData || buildFallbackProgress(challengeRes.data, []));
      }

      // If challenge is already in progress, both players are ready
      if (challengeRes.data.status === 'in_progress') {
        setOpponentConnected(true);
      }

      // Load opponent profile
      const isCreator = user.id === challengeRes.data.creator_id;
      const opponentId = isCreator ? challengeRes.data.opponent_id : challengeRes.data.creator_id;

      if (opponentId) {
        api.get(`/users/${opponentId}`)
          .then((res) => setOpponentProfile(res.data))
          .catch((err) => console.error('Failed to load opponent:', err));
      }

      setCurrentGameNumber((progressData || buildFallbackProgress(challengeRes.data, []))?.games_played + 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load challenge');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  /**
   * Fetch current game state from database
   * Used for polling to see if opponent rolled
   */
  const fetchGameState = async (gameId) => {
    try {
      const response = await api.get(
        `/challenges/${challengeId}/games/${gameId}/state`
      );
      
      const game = response.data;
      console.log('📊 Game state:', game);

      // Determine what rolls we have
      const isCreator = user.id === challenge.creator_id;
      const myRollValue = isCreator ? game.player1_roll : game.player2_roll;
      const opponentRollValue = isCreator ? game.player2_roll : game.player1_roll;

      // Update rolls
      setMyRoll(myRollValue);
      setOpponentRoll(opponentRollValue);

      // If both rolled and result determined, show result
      if (game.result_determined) {
        const yourRoll = myRollValue;
        const opponentRollVal = opponentRollValue;

        let message = '';
        let winner = '';
        
        if (yourRoll > opponentRollVal) {
          message = 'You win!';
          winner = isCreator ? 'creator' : 'opponent';
        } else if (opponentRollVal > yourRoll) {
          message = 'Opponent wins!';
          winner = isCreator ? 'opponent' : 'creator';
        } else {
          message = "It's a tie!";
          winner = 'tie';
        }

        const result = {
          yourRoll,
          opponentRoll: opponentRollVal,
          winner,
          message,
          isWinner: 
            (winner === 'creator' && isCreator) ||
            (winner === 'opponent' && !isCreator)
        };

        setGameResult(result);
        setGameState('result');

        // Refresh series progress so the scoreboard updates after refresh/roll.
        try {
          const progressRes = await api.get(`/challenges/${challengeId}/progress`);
          setProgress(progressRes.data);
        } catch (e) {
          console.error('Failed to refresh progress:', e);
          setProgress((prev) => prev || buildFallbackProgress(challenge, [game]));
        }

        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          console.log('⏹️ Stopped polling - result determined');
        }

        // Update user profile
        if (user && user.id) {
          api.get(`/users/${user.id}`)
            .then((res) => updateUser(res.data))
            .catch((err) => console.error('Failed to update profile:', err));
        }

        return;
      }

      // If both have rolled but result not yet determined, we're waiting
      if (myRollValue !== null && opponentRollValue !== null) {
        setGameState('rolled');
      } else if (myRollValue !== null) {
        // I rolled, waiting for opponent
        setGameState('rolled');
      } else {
        // Neither rolled yet
        setGameState('ready');
      }
    } catch (err) {
      console.error('Error fetching game state:', err);
    }
  };

  /**
   * Start polling for game state changes
   * Calls fetchGameState every 500ms
   */
  const startPolling = (gameId) => {
    // Avoid multiple polling intervals (important on refresh / re-fetch)
    stopPolling();
    console.log('⏱️ Starting to poll game state every 500ms');
    // Fetch immediately
    fetchGameState(gameId);
    
    // Then poll every 500ms
    pollingIntervalRef.current = setInterval(() => {
      fetchGameState(gameId);
    }, 500);
  };

  /**
   * Stop polling
   */
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const startWaitingPoll = () => {
    if (waitForOpponentRef.current) return;
    waitForOpponentRef.current = setInterval(() => {
      fetchChallengeData({ silent: true });
    }, 2000);
  };

  const stopWaitingPoll = () => {
    if (waitForOpponentRef.current) {
      clearInterval(waitForOpponentRef.current);
      waitForOpponentRef.current = null;
    }
  };

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      stopWaitingPoll();
    };
  }, []);

  useEffect(() => {
    const isPending = challenge?.status === 'pending';
    if (isPending && !currentGame) {
      startWaitingPoll();
      return;
    }
    stopWaitingPoll();
  }, [challenge?.status, currentGame]);

  // After refresh (or any re-fetch), sync the UI to the database game state.
  // This prevents the player from seeing the "rolling/ready" UI even though they already rolled.
  useEffect(() => {
    if (!user || !challenge || !currentGame) return;
    if (challenge.status === 'completed') return;

    startPolling(currentGame.id);
  }, [user, challenge, currentGame]);

  const handleRoll = async () => {
    try {
      if (!currentGame) {
        console.error('❌ currentGame is null:', { currentGame, challenge, progress });
        setError('Game not initialized. Please refresh the page.');
        return;
      }

      console.log(`🎲 Rolling with game:`, currentGame);
      setGameState('rolling');
      setIsWaitingForOpponent(true);

      // Call REST API to record roll
      const response = await api.post(
        `/challenges/${challengeId}/games/${currentGame.id}/roll`
      );

      console.log('🎲 Roll response:', response.data);

      const payload = response.data || {};
      const game = payload.gameState || payload.result || payload;
      const isCreator = user.id === challenge.creator_id;
      const myRollValue = isCreator ? game.player1_roll : game.player2_roll;
      const opponentRollValue = isCreator ? game.player2_roll : game.player1_roll;

      setMyRoll(myRollValue);
      setOpponentRoll(opponentRollValue);

      // If result already determined (opponent rolled same time), show result
      if (payload.resultDetermined || game.result_determined) {
        let message = '';
        let winner = '';
        
        if (myRollValue > opponentRollValue) {
          message = 'You win!';
          winner = isCreator ? 'creator' : 'opponent';
        } else if (opponentRollValue > myRollValue) {
          message = 'Opponent wins!';
          winner = isCreator ? 'opponent' : 'creator';
        } else {
          message = "It's a tie!";
          winner = 'tie';
        }

        const result = {
          yourRoll: myRollValue,
          opponentRoll: opponentRollValue,
          winner,
          message,
          isWinner: 
            (winner === 'creator' && isCreator) ||
            (winner === 'opponent' && !isCreator)
        };

        setGameResult(result);
        setGameState('result');
        setIsWaitingForOpponent(false);

        // Refresh series progress so the scoreboard updates immediately.
        try {
          const progressRes = await api.get(`/challenges/${challengeId}/progress`);
          setProgress(progressRes.data);
        } catch (e) {
          console.error('Failed to refresh progress:', e);
          setProgress((prev) => prev || buildFallbackProgress(challenge, [game]));
        }

        // Update user profile
        if (user && user.id) {
          api.get(`/users/${user.id}`)
            .then((res) => updateUser(res.data))
            .catch((err) => console.error('Failed to update profile:', err));
        }
      } else {
        // Opponent hasn't rolled yet, start polling
        setGameState('rolled');
        startPolling(currentGame.id);
      }
    } catch (err) {
      console.error('Error rolling:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to roll';
      console.error('📋 Error details:', { status: err.response?.status, error: errorMsg });

      // If the roll was already recorded (common after refresh), refresh state from DB
      // instead of trapping the user on an error page.
      const normalized = String(errorMsg).toLowerCase();
      if (
        normalized.includes('already rolled') ||
        normalized.includes('dice has been roll') ||
        normalized.includes('dice has been rolled')
      ) {
        setError(null);
        setIsWaitingForOpponent(true);
        startPolling(currentGame.id);
        return;
      }

      setError(errorMsg);
      setGameState('ready');
      setIsWaitingForOpponent(false);
    }
  };

  const handleNextGame = async () => {
    try {
      console.log(`📋 Creating next game... current game #${currentGameNumber}/${challenge.num_games}`);
      
      // Stop polling if still active
      stopPolling();

      // Get current game
      if (!currentGame) {
        console.error('❌ No current game to get next from');
        return;
      }

      // Create next game via REST API
      const response = await api.post(
        `/challenges/${challengeId}/next-game`
      );

      const newGame = response.data;
      console.log(`✓ Next game created:`, newGame);
      setCurrentGame(newGame);
      
      // Reset for next game
      setMyRoll(null);
      setOpponentRoll(null);
      setGameResult(null);
      setGameState('ready');
      setCurrentGameNumber(currentGameNumber + 1);
      setIsWaitingForOpponent(false);
      console.log(`✓ Reset UI for game #${currentGameNumber + 1}`);

      // Refresh progress
      try {
        const progressRes = await api.get(`/challenges/${challengeId}/progress`);
        setProgress(progressRes.data);
        console.log(`✓ Progress updated:`, progressRes.data);
      } catch (progressErr) {
        const fallbackProgress = buildFallbackProgress(challenge, [newGame]);
        setProgress(fallbackProgress);
        console.warn('Failed to refresh challenge progress after creating next game:', progressErr);
      }
    } catch (err) {
      console.error('❌ Error creating next game:', err);
      setError(err.response?.data?.error || 'Failed to create next game');
    }
  };

  const handleSeriesComplete = async () => {
    try {
      // Mark challenge as completed in backend
      await api.post(`/challenges/${challengeId}/complete`);
    } catch (err) {
      console.error('Warning: Failed to mark challenge complete:', err);
      // Don't block navigation if completion fails
    }
    navigate(`/series-complete/${challengeId}`);
  };

  const handleForfeit = async () => {
    if (!window.confirm('Are you sure? You will lose this challenge and forfeit your wager.')) {
      return;
    }

    try {
      await api.post(`/challenges/${challengeId}/forfeit`);
      stopPolling();
      setGameState('finished');
      setIsWaitingForOpponent(false);
      navigate('/challenges', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to forfeit challenge');
    }
  };

  if (loading) {
    return (
      <div className="chess-match-page">
        <div className="cm-topbar">
          <button className="cm-icon-btn" onClick={() => navigate('/challenges')}>←</button>
          <div className="cm-topbar-center"><span className="cm-topbar-title">Challenge</span></div>
          <span style={{ width: 36 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }}>Loading challenge…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chess-match-page">
        <div className="cm-topbar">
          <button className="cm-icon-btn" onClick={() => navigate('/challenges')}>←</button>
          <div className="cm-topbar-center"><span className="cm-topbar-title">Challenge</span></div>
          <span style={{ width: 36 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="cm-toast cm-toast--error" style={{ margin: 24 }}>{error}</div>
        </div>
      </div>
    );
  }

  if (!challenge || !progress) {
    return (
      <div className="chess-match-page">
        <div className="cm-topbar">
          <button className="cm-icon-btn" onClick={() => navigate('/challenges')}>←</button>
          <div className="cm-topbar-center"><span className="cm-topbar-title">Challenge</span></div>
          <span style={{ width: 36 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }}>Challenge not found</div>
      </div>
    );
  }

  // If challenge has been forfeited/completed, don’t render the interactive dice game UI.
  if (challenge.status === 'completed') {
    const grossPot = Number(challenge.wager || 0) * Number(challenge.num_games || 1) * 2;
    const chargePercent = Number.isFinite(Number(challenge.charge_percent)) ? Number(challenge.charge_percent) : 0;
    const chargeAmount = Number.isFinite(Number(challenge.charge_amount))
      ? Number(challenge.charge_amount)
      : Math.floor(grossPot * (chargePercent / 100));
    const netPot = Number.isFinite(Number(challenge.net_pot))
      ? Number(challenge.net_pot)
      : Math.max(0, grossPot - chargeAmount);
    return (
      <div className="chess-match-page">
        <div className="cm-topbar">
          <button className="cm-icon-btn" onClick={() => navigate('/challenges', { replace: true })}>←</button>
          <div className="cm-topbar-center">
            <span className="cm-topbar-title">Challenge Series</span>
            <span className="cm-badge cm-badge--gold">💰 {challenge.wager}</span>
          </div>
          <span style={{ width: 36 }} />
        </div>
        <div className="cm-result-banner cm-result-banner--draw">
          <span className="cm-result-banner__icon">🏁</span>
          <span>Challenge Ended</span>
        </div>
        <div className="cm-status-line">
          Pot {grossPot} · Charge {chargeAmount} ({chargePercent}%) · Net {netPot}
        </div>
        <div className="cm-actions">
          <button className="cm-btn cm-btn--primary" onClick={() => navigate('/challenges', { replace: true })}>Back to Challenges</button>
        </div>
      </div>
    );
  }

  const isCreator = user.id === challenge.creator_id;
  const opponentName = isCreator ? challenge.opponent_username : challenge.creator_username;
  const opponentIsAi = isCreator ? Boolean(challenge.opponent_is_ai) : Boolean(challenge.creator_is_ai);
  const seriesComplete = progress.games_played >= challenge.num_games;
  const hasAnyRoll = myRoll !== null || opponentRoll !== null;
  const canForfeit = isCreator && progress.games_played === 0 && !hasAnyRoll;
  const mySeriesScore = isCreator ? parseInt(progress.creator_wins || 0, 10) : parseInt(progress.opponent_wins || 0, 10);
  const opponentSeriesScore = isCreator ? parseInt(progress.opponent_wins || 0, 10) : parseInt(progress.creator_wins || 0, 10);
  const seriesTies = parseInt(progress.ties || 0, 10);
  const shouldShowGameContent = !seriesComplete || (gameState === 'result' && !!gameResult);
  
  console.log(`📊 Challenge Status Check:`, {
    games_played: progress.games_played,
    num_games: challenge.num_games,
    seriesComplete,
  });

  return (
    <div className="chess-match-page">
      {/* ── Top bar ── */}
      <div className="cm-topbar">
        <button className="cm-icon-btn" onClick={() => navigate('/challenges')}>←</button>
        <div className="cm-topbar-center">
          <span className="cm-topbar-title">Game {progress.games_played + 1}/{challenge.num_games}</span>
          <span className="cm-badge cm-badge--gold">💰 {challenge.wager}</span>
        </div>
        {canForfeit ? (
          <button className="cm-icon-btn" onClick={handleForfeit} title="Forfeit" style={{ color: '#fca5a5' }}>✕</button>
        ) : <span style={{ width: 36 }} />}
      </div>

      {/* ── Opponent bar (top) ── */}
      <div className={`cm-player-bar${gameState === 'result' && gameResult ? '' : ' cm-player-bar--active'}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">🎲</span>
          <span className="cm-player-bar__name">{opponentName || 'Opponent'}{opponentIsAi ? ' • AI Player' : ''}</span>
        </div>
        <div className="dice-series-score">{opponentSeriesScore}</div>
      </div>

      {/* ── Dice arena ── */}
      {seriesComplete && !shouldShowGameContent ? (
        <div className="dice-arena">
          <div className="dice-arena__status">
            <span style={{ fontSize: 32, marginBottom: 8 }}>🏁</span>
            <span style={{ fontWeight: 700, fontSize: 16 }}>Series Complete!</span>
            <button className="cm-btn cm-btn--primary" onClick={handleSeriesComplete} style={{ marginTop: 12 }}>View Results</button>
          </div>
        </div>
      ) : (
        <div className="dice-arena">
          {gameState === 'waiting' && (
            <div className="dice-arena__status">
              <div className="dice-arena__spinner">🎲</div>
              <span>Waiting for opponent…</span>
            </div>
          )}

          {gameState === 'ready' && (
            <div className="dice-arena__status">
              <span style={{ color: '#86efac' }}>Ready to roll!</span>
              <button className="cm-btn cm-btn--primary dice-roll-btn" onClick={handleRoll}>Roll Dice</button>
            </div>
          )}

          {gameState === 'rolling' && (
            <div className="dice-arena__status">
              <div className="dice-arena__spinner">🎲</div>
              <span>Rolling…</span>
            </div>
          )}

          {gameState === 'rolled' && !gameResult && (
            <div className="dice-arena__status">
              <div className="dice-roll-value dice-roll-value--big">{myRoll}</div>
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>Waiting for opponent…</span>
            </div>
          )}

          {gameState === 'result' && gameResult && (
            <div className="dice-arena__versus">
              <div className="dice-arena__col">
                <div className="dice-roll-value dice-roll-value--big">{gameResult.yourRoll}</div>
                <span className="dice-arena__label">{user.username}</span>
              </div>
              <span className="dice-arena__vs">VS</span>
              <div className="dice-arena__col">
                <div className="dice-roll-value dice-roll-value--big">{gameResult.opponentRoll}</div>
                <span className="dice-arena__label">{opponentName}{opponentIsAi ? ' • AI Player' : ''}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── You bar (bottom) ── */}
      <div className={`cm-player-bar${gameState !== 'result' ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">🎲</span>
          <span className="cm-player-bar__name">{user.username}</span>
        </div>
        <div className="dice-series-score">{mySeriesScore}</div>
      </div>

      {/* ── Result banner ── */}
      {gameState === 'result' && gameResult && (() => {
        const isWin = gameResult.isWinner;
        const isTie = gameResult.winner === 'tie';
        const cls = isTie ? 'cm-result-banner--draw' : isWin ? 'cm-result-banner--win' : 'cm-result-banner--loss';
        return (
          <div className={`cm-result-banner ${cls}`}>
            <span className="cm-result-banner__icon">{isTie ? '🤝' : isWin ? '🏆' : '😞'}</span>
            <span>{gameResult.message}</span>
          </div>
        );
      })()}

      {/* ── Ties badge ── */}
      {seriesTies > 0 && <div className="cm-status-line">{seriesTies} tie{seriesTies !== 1 ? 's' : ''}</div>}

      {/* ── Actions ── */}
      <div className="cm-actions">
        {gameState === 'result' && gameResult && !seriesComplete && (
          <button className="cm-btn cm-btn--primary" onClick={handleNextGame}>Next Game</button>
        )}
        {gameState === 'result' && gameResult && seriesComplete && (
          <button className="cm-btn cm-btn--primary" onClick={handleSeriesComplete}>View Results</button>
        )}
      </div>

      {error && <div className="cm-toast cm-toast--error">{error}</div>}
    </div>
  );
}

export default ChallengeGame;

